import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { fetchWithFormData } from '../util/fetchWithFormData';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { formatCurrency } from '../util/formatCurrency';
import {
  ImportExternalTransactionsSummary,
  ImportExternalTransactionsRow,
  ExternalFinancialTransactionImport,
} from '../types';

const FINANCE_SOURCE = 'tini_express_fnb_eswatini';
const FINANCE_SOURCE_LABEL = 'Tini Express FNB Eswatini';

const formatAmount = (raw: string) => {
  const n = parseFloat(raw);
  if (isNaN(n)) return raw;
  const formatted = formatCurrency(Math.abs(n));
  return n < 0 ? `-${formatted}` : formatted;
};

const formatDate = (raw: string) => {
  if (!raw) return '';
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(raw);
  const d = new Date(isDateOnly ? `${raw}T00:00:00` : raw);
  return isNaN(d.getTime()) ? raw : d.toLocaleDateString();
};

type Mode = 'idle' | 'preview' | 'result';

const ImportExternalTransactions = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [mode, setMode] = useState<Mode>('idle');
  const [preview, setPreview] = useState<ImportExternalTransactionsSummary | null>(null);
  const [result, setResult] = useState<ImportExternalTransactionsSummary | null>(null);
  const [working, setWorking] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [recentImports, setRecentImports] = useState<ExternalFinancialTransactionImport[]>([]);
  const [recentImportsLoading, setRecentImportsLoading] = useState(false);

  const fetchRecentImports = useCallback(async () => {
    setRecentImportsLoading(true);
    try {
      const res = await fetchWithAuth('/external_financial_transactions/imports');
      if (res.ok) {
        const data = await res.json();
        setRecentImports(data.external_financial_transaction_imports || []);
      }
    } catch (err) {
      console.error('Failed to fetch recent imports:', err);
    } finally {
      setRecentImportsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecentImports();
  }, [fetchRecentImports]);

  const reset = () => {
    setSelectedFile(null);
    setPreview(null);
    setResult(null);
    setErrorMessage(null);
    setMode('idle');
    setFileInputKey((k) => k + 1);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
    setPreview(null);
    setResult(null);
    setErrorMessage(null);
    setMode('idle');
  };

  const callImporter = async (file: File, dryRun: boolean): Promise<ImportExternalTransactionsSummary | null> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('finance_source', FINANCE_SOURCE);
    formData.append('dry_run', dryRun ? 'true' : 'false');

    const res = await fetchWithFormData('/external_financial_transactions/imports', formData);
    const body = await res.json().catch(() => ({}));

    if (!res.ok) {
      setErrorMessage(body.error || 'Import failed');
      return null;
    }
    return body as ImportExternalTransactionsSummary;
  };

  const handlePreview = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) return;

    setWorking(true);
    setErrorMessage(null);
    setPreview(null);
    setResult(null);

    const data = await callImporter(selectedFile, true);
    if (data) {
      setPreview(data);
      setMode('preview');
    }
    setWorking(false);
  };

  const handleConfirm = async () => {
    if (!selectedFile) return;

    setWorking(true);
    setErrorMessage(null);

    const data = await callImporter(selectedFile, false);
    if (data) {
      setResult(data);
      setMode('result');
      toast.success(`Imported ${data.created} transaction${data.created === 1 ? '' : 's'}`);
      fetchRecentImports();
    }
    setWorking(false);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Import Bank Statement</h1>
        <p className="text-gray-600">
          Upload a CSV export from a bank statement. We will show a preview first; nothing is saved until you confirm.
        </p>
      </div>

      <form onSubmit={handlePreview} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 space-y-4">
        <div>
          <label htmlFor="financeSource" className="block text-sm font-medium text-gray-700 mb-1">
            Source
          </label>
          <select
            id="financeSource"
            value={FINANCE_SOURCE}
            disabled
            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-700 cursor-not-allowed"
          >
            <option value={FINANCE_SOURCE}>{FINANCE_SOURCE_LABEL}</option>
          </select>
        </div>

        <div>
          <label htmlFor="csvFile" className="block text-sm font-medium text-gray-700 mb-1">
            CSV File
          </label>
          <input
            key={fileInputKey}
            type="file"
            id="csvFile"
            accept=".csv"
            onChange={handleFileChange}
            disabled={mode === 'preview' || working}
            className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50"
          />
          {selectedFile && (
            <p className="mt-1 text-xs text-gray-500">Selected: {selectedFile.name}</p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            type="submit"
            disabled={!selectedFile || working || mode === 'preview' || mode === 'result'}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {working && mode === 'idle' ? 'Building preview…' : 'Preview Import'}
          </button>
          {(mode === 'preview' || mode === 'result') && (
            <button
              type="button"
              onClick={reset}
              className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Start over
            </button>
          )}
        </div>
      </form>

      {errorMessage && (
        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded">
          <p className="text-sm text-red-800">{errorMessage}</p>
        </div>
      )}

      {mode === 'preview' && preview && (
        <PreviewPanel
          summary={preview}
          working={working}
          onConfirm={handleConfirm}
          onCancel={reset}
        />
      )}

      {mode === 'result' && result && <ResultPanel summary={result} />}

      <RecentImportsPanel imports={recentImports} loading={recentImportsLoading} />
    </div>
  );
};

type PreviewPanelProps = {
  summary: ImportExternalTransactionsSummary;
  working: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

const PreviewPanel = ({ summary, working, onConfirm, onCancel }: PreviewPanelProps) => {
  const nothingToImport = summary.rows_to_create.length === 0;
  const dbDuplicates = summary.duplicate_rows.filter((r) => r.existing_id != null);
  const fileDuplicates = summary.duplicate_rows.filter((r) => r.existing_id == null);

  return (
    <div className="mt-6 bg-white p-6 rounded-lg shadow-sm border border-gray-200 space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Preview</h2>
        <p className="text-sm text-gray-600">Nothing has been saved yet. Review the rows below and click Confirm to import.</p>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <SummaryCard label="Will import" count={summary.rows_to_create.length} tone="green" />
        <SummaryCard label="Already in DB" count={dbDuplicates.length} tone="yellow" />
        <SummaryCard label="Duplicate in file" count={fileDuplicates.length} tone="yellow" />
        <SummaryCard label="Errors" count={summary.errors.length} tone="red" />
      </div>

      <RowSection title="New transactions (will be imported)" rows={summary.rows_to_create} columns="basic" emptyHint="No new transactions in this file." />
      <RowSection title="Already in database (will be skipped)" rows={dbDuplicates} columns="with_existing_id" emptyHint="No matching rows in the database." />
      <RowSection title="Duplicate within this file (will be skipped)" rows={fileDuplicates} columns="basic" emptyHint="No repeated rows in the file." />
      <ErrorSection errors={summary.errors} />

      <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
        <button
          type="button"
          onClick={onConfirm}
          disabled={working || nothingToImport}
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {working ? 'Importing…' : `Confirm import (${summary.rows_to_create.length})`}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={working}
          className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        {nothingToImport && (
          <span className="text-sm text-gray-500">Nothing new to import — every row in this file already exists.</span>
        )}
      </div>
    </div>
  );
};

const ResultPanel = ({ summary }: { summary: ImportExternalTransactionsSummary }) => (
  <div className="mt-6 bg-white p-6 rounded-lg shadow-sm border border-gray-200 space-y-4">
    <div>
      <h2 className="text-lg font-semibold text-gray-900 mb-1">Import complete</h2>
      <p className="text-sm text-gray-600">
        Created {summary.created} transaction{summary.created === 1 ? '' : 's'}; skipped {summary.skipped_duplicates} duplicate{summary.skipped_duplicates === 1 ? '' : 's'}.
      </p>
    </div>

    <div className="grid grid-cols-3 gap-4">
      <SummaryCard label="Created" count={summary.created} tone="green" />
      <SummaryCard label="Skipped duplicates" count={summary.skipped_duplicates} tone="yellow" />
      <SummaryCard label="Errors" count={summary.errors.length} tone="red" />
    </div>

    {summary.errors.length > 0 && <ErrorSection errors={summary.errors} />}

    <div>
      <Link
        to={summary.import_id ? `/external-financial-transactions?import_id=${summary.import_id}` : '/external-financial-transactions'}
        className="inline-block bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
      >
        View imported transactions
      </Link>
    </div>
  </div>
);

const toneClasses: Record<string, string> = {
  green: 'bg-green-50 border-green-200 text-green-900',
  yellow: 'bg-yellow-50 border-yellow-200 text-yellow-900',
  red: 'bg-red-50 border-red-200 text-red-900',
};

const SummaryCard = ({ label, count, tone }: { label: string; count: number; tone: 'green' | 'yellow' | 'red' }) => (
  <div className={`p-3 rounded border ${toneClasses[tone]}`}>
    <div className="text-xs uppercase tracking-wide">{label}</div>
    <div className="text-2xl font-bold">{count}</div>
  </div>
);

type RowSectionProps = {
  title: string;
  rows: ImportExternalTransactionsRow[];
  columns: 'basic' | 'with_existing_id';
  emptyHint: string;
};

const RowSection = ({ title, rows, columns, emptyHint }: RowSectionProps) => (
  <div>
    <h3 className="text-sm font-semibold text-gray-900 mb-2">
      {title} ({rows.length})
    </h3>
    {rows.length === 0 ? (
      <p className="text-sm text-gray-500">{emptyHint}</p>
    ) : (
      <div className="max-h-72 overflow-y-auto border border-gray-200 rounded">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Row</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
              {columns === 'with_existing_id' && (
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Existing ID</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {rows.map((row) => {
              const amountValue = parseFloat(row.amount);
              const amountClass = !isNaN(amountValue) && amountValue < 0 ? 'text-red-600' : 'text-gray-900';
              return (
                <tr key={`${row.row_number}-${row.description}`}>
                  <td className="px-3 py-2 text-gray-700">{row.row_number}</td>
                  <td className="px-3 py-2 text-gray-900 whitespace-nowrap">{formatDate(row.date)}</td>
                  <td className={`px-3 py-2 whitespace-nowrap font-mono ${amountClass}`}>{formatAmount(row.amount)}</td>
                  <td className="px-3 py-2 whitespace-nowrap font-mono text-gray-500">{formatAmount(row.balance)}</td>
                  <td className="px-3 py-2 text-gray-700">{row.description}</td>
                  {columns === 'with_existing_id' && (
                    <td className="px-3 py-2 text-gray-500">{row.existing_id}</td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const ErrorSection = ({ errors }: { errors: { row_number: number; message: string }[] }) => (
  <div>
    <h3 className="text-sm font-semibold text-gray-900 mb-2">Errors ({errors.length})</h3>
    {errors.length === 0 ? (
      <p className="text-sm text-gray-500">No malformed rows.</p>
    ) : (
      <div className="max-h-48 overflow-y-auto border border-gray-200 rounded">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Row</th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {errors.map((err, idx) => (
              <tr key={`${err.row_number}-${idx}`}>
                <td className="px-3 py-2 text-gray-900">{err.row_number}</td>
                <td className="px-3 py-2 text-gray-700">{err.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

type RecentImportsPanelProps = {
  imports: ExternalFinancialTransactionImport[];
  loading: boolean;
};

const RecentImportsPanel = ({ imports, loading }: RecentImportsPanelProps) => {
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const toggleExpanded = (id: number) => {
    setExpandedId(prev => (prev === id ? null : id));
  };

  return (
    <div className="mt-8 bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Recent Imports</h2>
        <p className="text-sm text-gray-600">The 20 most recent statement imports. Click a row to see the transactions it created.</p>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : imports.length === 0 ? (
        <p className="text-sm text-gray-500">No imports yet.</p>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Admin</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Skipped</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Errors</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {imports.map((imp) => {
                const isExpanded = expandedId === imp.id;
                const hasErrors = imp.errors_count > 0;
                return (
                  <React.Fragment key={imp.id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-900 whitespace-nowrap">
                        {new Date(imp.created_at).toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-gray-700">{imp.created_by || '—'}</td>
                      <td className="px-3 py-2 text-gray-700">{imp.finance_source_label}</td>
                      <td className="px-3 py-2 text-right font-mono text-gray-900">{imp.created_count}</td>
                      <td className="px-3 py-2 text-right font-mono text-gray-500">{imp.skipped_duplicates_count}</td>
                      <td className="px-3 py-2 text-right">
                        {hasErrors ? (
                          <button
                            type="button"
                            onClick={() => toggleExpanded(imp.id)}
                            className="font-mono text-red-700 hover:underline"
                            aria-expanded={isExpanded}
                            aria-label={`${isExpanded ? 'Hide' : 'Show'} error details for import ${imp.id}`}
                          >
                            {imp.errors_count} {isExpanded ? '▾' : '▸'}
                          </button>
                        ) : (
                          <span className="font-mono text-gray-400">0</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-500 truncate max-w-xs" title={imp.original_filename || ''}>
                        {imp.original_filename || '—'}
                      </td>
                      <td className="px-3 py-2">
                        <Link
                          to={`/external-financial-transactions?import_id=${imp.id}`}
                          className="text-blue-600 hover:underline whitespace-nowrap"
                        >
                          View {imp.created_count} →
                        </Link>
                      </td>
                    </tr>
                    {isExpanded && hasErrors && (
                      <tr className="bg-red-50">
                        <td colSpan={8} className="px-3 py-3">
                          <div className="text-xs font-semibold text-red-800 uppercase tracking-wider mb-2">Errors</div>
                          <ul className="space-y-1 text-sm text-red-900">
                            {imp.errors.map((err, idx) => (
                              <li key={`${imp.id}-${err.row_number}-${idx}`}>
                                <span className="font-mono">Row {err.row_number}</span>: {err.message}
                              </li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ImportExternalTransactions;
